import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Clock, AlertTriangle, CreditCard, Wallet,
  Search, ChevronDown, ChevronRight, Check, X, Trash2,
  Ban, RefreshCw, ExternalLink, MoreHorizontal, Euro, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../utils/format';
import api from '../services/api';

const FILTER_OPTIONS = [
  { id: 'all', label: 'Tous' },
  { id: 'upcoming', label: 'À encaisser' },
  { id: 'paid_done', label: 'Encaissés' },
  { id: 'late', label: 'En retard' },
  { id: 'this_month', label: 'Ce mois' },
  { id: 'next_month', label: 'Mois prochain' },
  { id: 'deposit', label: 'Acomptes' },
  { id: 'initial', label: 'Initiaux' },
  { id: 'recurring', label: 'Récurrents' },
];

const PLAN_STATUS_CFG = {
  cancelled: { label: 'Annulé', cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
  completed: { label: 'Complété', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  late: { label: 'En retard', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  active: { label: 'Actif', cls: 'bg-[#1B99E5]/10 text-[#1B99E5]' },
};

const LINE_STATUS_CFG = {
  upcoming: { label: 'À venir', cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  paid: { label: 'Payé', cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  late: { label: 'En retard', cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  cancelled: { label: 'Annulé', cls: 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
};

const TYPE_CFG = {
  deposit: { label: 'Acompte', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  initial: { label: 'Initial', cls: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  recurring: { label: 'Récurrent', cls: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
};

const FREQ_LABELS = { monthly: '/mois', quarterly: '/trim.', annual: '/an' };

function KpiCard({ label, value, sub, icon: Icon, iconBg, iconColor }) {
  return (
    <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate">{label}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white mt-1 truncate">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon size={16} className={iconColor} />
        </div>
      </div>
    </div>
  );
}

export default function Paiements() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [markPaidLine, setMarkPaidLine] = useState(null);
  const [markPaidForm, setMarkPaidForm] = useState({ paid_date: '', paid_amount: 0 });
  const [savingMarkPaid, setSavingMarkPaid] = useState(false);
  const [deletePlanData, setDeletePlanData] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [cancelLineData, setCancelLineData] = useState(null);
  const [cancellingLine, setCancellingLine] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, plansRes] = await Promise.all([
        api.get('/payment-stats'),
        api.get('/payment-plans')
      ]);
      setStats(statsRes.data);
      setPlans(plansRes.data);
    } catch { toast.error('Erreur de chargement des données'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const filteredPlans = useMemo(() => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString().split('T')[0];
    const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().split('T')[0];

    return plans.filter(plan => {
      if (search) {
        const s = search.toLowerCase();
        const hay = [plan.deal_title, plan.contact_name, plan.company_name, plan.offer_name].map(v => v || '').join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      const lines = plan.schedule_lines || [];
      switch (activeFilter) {
        case 'all': return true;
        case 'upcoming': return lines.some(l => ['upcoming', 'late'].includes(l.status));
        case 'paid_done': return plan.overall_status === 'completed';
        case 'late': return plan.has_late;
        case 'this_month': return lines.some(l => ['upcoming', 'late'].includes(l.status) && l.due_date >= thisMonthStart && l.due_date <= thisMonthEnd);
        case 'next_month': return lines.some(l => l.status === 'upcoming' && l.due_date >= nextMonthStart && l.due_date <= nextMonthEnd);
        case 'deposit': return plan.has_deposit;
        case 'initial': return plan.has_initial;
        case 'recurring': return plan.has_recurring;
        default: return true;
      }
    });
  }, [plans, activeFilter, search]);

  const openMarkPaid = (line) => {
    setMarkPaidLine(line);
    setMarkPaidForm({ paid_date: new Date().toISOString().split('T')[0], paid_amount: line.amount });
  };

  const handleMarkPaid = async () => {
    if (!markPaidLine) return;
    setSavingMarkPaid(true);
    try {
      await api.put(`/payment-schedules/${markPaidLine.id}`, {
        status: 'paid',
        paid_date: markPaidForm.paid_date,
        paid_amount: parseFloat(markPaidForm.paid_amount)
      });
      toast.success('Paiement enregistré');
      setMarkPaidLine(null);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSavingMarkPaid(false); }
  };

  const handleCancelLine = async () => {
    if (!cancelLineData) return;
    setCancellingLine(true);
    try {
      await api.put(`/payment-schedules/${cancelLineData.id}`, { status: 'cancelled' });
      toast.success('Échéance annulée');
      setCancelLineData(null);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setCancellingLine(false); }
  };

  const openDeletePlan = (plan) => {
    const hasPaid = (plan.schedule_lines || []).some(l => l.status === 'paid');
    setDeletePlanData({ plan, hasPaid });
  };

  const handleDeleteOrCancelPlan = async () => {
    if (!deletePlanData) return;
    setDeleting(true);
    try {
      if (deletePlanData.hasPaid) {
        await api.patch(`/payment-plans/${deletePlanData.plan.id}/cancel`);
        toast.success('Plan annulé — l\'historique est conservé');
      } else {
        await api.delete(`/payment-plans/${deletePlanData.plan.id}`);
        toast.success('Plan supprimé définitivement');
      }
      setDeletePlanData(null);
      if (expandedPlanId === deletePlanData.plan.id) setExpandedPlanId(null);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setDeleting(false); }
  };

  const getPlanStatusDisplay = (plan) => {
    const key = plan.status === 'cancelled' ? 'cancelled' : plan.overall_status;
    return PLAN_STATUS_CFG[key] || PLAN_STATUS_CFG.active;
  };

  if (loading) return (
    <div className="p-6 flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B99E5]"></div>
    </div>
  );

  const kpiCards = stats ? [
    { label: 'Encaissé ce mois', value: formatCurrency(stats.collected_this_month), icon: TrendingUp, iconBg: 'bg-green-50 dark:bg-green-900/20', iconColor: 'text-green-600 dark:text-green-400' },
    { label: 'À encaisser ce mois', value: formatCurrency(stats.due_this_month), icon: Clock, iconBg: 'bg-blue-50 dark:bg-blue-900/20', iconColor: 'text-blue-600 dark:text-blue-400' },
    { label: 'En retard', value: formatCurrency(stats.late_total), sub: `${stats.late_count} échéance${stats.late_count !== 1 ? 's' : ''}`, icon: AlertTriangle, iconBg: 'bg-red-50 dark:bg-red-900/20', iconColor: 'text-red-600 dark:text-red-400' },
    { label: 'Reste à encaisser', value: formatCurrency(stats.remaining), icon: Wallet, iconBg: 'bg-orange-50 dark:bg-orange-900/20', iconColor: 'text-orange-600 dark:text-orange-400' },
    { label: 'Valeur contrats actifs', value: formatCurrency(stats.total_contracts), icon: CreditCard, iconBg: 'bg-[#1B99E5]/10', iconColor: 'text-[#1B99E5]' },
  ] : [];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Paiements</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{filteredPlans.length} plan{filteredPlans.length !== 1 ? 's' : ''} · Suivi des encaissements</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {kpiCards.map(card => <KpiCard key={card.label} {...card} />)}
      </div>

      {/* Filters + Search */}
      <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher client, entreprise, offre, devis..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-8 text-sm border-gray-200 dark:border-gray-700"
              data-testid="payments-search"
            />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto flex-nowrap">
            {FILTER_OPTIONS.map(f => (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap flex-shrink-0 transition-all ${
                  activeFilter === f.id
                    ? 'bg-[#1B99E5] text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Plans Table */}
      {filteredPlans.length === 0 ? (
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <CreditCard size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aucun plan de paiement</p>
          <p className="text-xs text-gray-400 mt-1">Les plans sont créés depuis la fiche d'un devis gagné.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          {/* Table header */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                  {['Client / Devis', 'Entreprise', 'Offre', 'Valeur contrat', 'Acompte', 'Initial', 'Récurrent', 'Encaissé', 'Reste à enc.', 'Proch. échéance', 'Statut', ''].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPlans.map(plan => {
                  const isExpanded = expandedPlanId === plan.id;
                  const statusCfg = getPlanStatusDisplay(plan);
                  const hasPaid = (plan.schedule_lines || []).some(l => l.status === 'paid');
                  return (
                    <React.Fragment key={plan.id}>
                      {/* Plan row */}
                      <tr
                        className={`border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50/70 dark:hover:bg-gray-800/30 transition-colors ${plan.has_late && plan.status !== 'cancelled' ? 'border-l-2 border-l-red-400' : ''}`}
                        onClick={() => setExpandedPlanId(isExpanded ? null : plan.id)}
                        data-testid={`plan-row-${plan.id}`}
                      >
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 dark:text-gray-600 flex-shrink-0">
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[140px]">{plan.contact_name || '—'}</p>
                              <p className="text-xs text-gray-400 truncate max-w-[140px]">{plan.deal_title}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[120px] truncate">{plan.company_name || '—'}</td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[100px] truncate">{plan.offer_name || '—'}</td>
                        <td className="px-3 py-3 text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(plan.total_contract_value)}</td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{plan.has_deposit ? formatCurrency(plan.deposit_amount) : '—'}</td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{plan.has_initial ? formatCurrency(plan.initial_amount) : '—'}</td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {plan.has_recurring ? `${formatCurrency(plan.recurring_amount)}${FREQ_LABELS[plan.recurring_frequency] || ''}` : '—'}
                        </td>
                        <td className="px-3 py-3 text-sm font-medium text-green-700 dark:text-green-400 whitespace-nowrap">{formatCurrency(plan.total_collected)}</td>
                        <td className="px-3 py-3 text-sm font-medium text-orange-700 dark:text-orange-400 whitespace-nowrap">{formatCurrency(plan.remaining)}</td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {plan.next_due_date ? (
                            <span className={plan.has_late ? 'text-red-600 font-medium' : ''}>{formatDate(plan.next_due_date)}</span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${statusCfg.cls}`}>{statusCfg.label}</span>
                        </td>
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700" data-testid={`plan-actions-${plan.id}`}>
                                <MoreHorizontal size={14} className="text-gray-400" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => navigate(`/deals/${plan.deal_id}`)}>
                                <ExternalLink size={13} className="mr-2" /> Voir le devis
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {plan.status !== 'cancelled' && (
                                <DropdownMenuItem
                                  className={hasPaid ? 'text-orange-600 focus:text-orange-600' : 'text-red-600 focus:text-red-600'}
                                  onClick={() => openDeletePlan(plan)}
                                  data-testid={`plan-delete-${plan.id}`}
                                >
                                  {hasPaid ? <><Ban size={13} className="mr-2" /> Annuler le plan</> : <><Trash2 size={13} className="mr-2" /> Supprimer le plan</>}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>

                      {/* Expanded schedule */}
                      {isExpanded && (
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          <td colSpan={12} className="px-0 py-0">
                            <div className="bg-gray-50/80 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-700 px-6 py-3">
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                Échéancier — {(plan.schedule_lines || []).length} ligne{(plan.schedule_lines || []).length !== 1 ? 's' : ''}
                              </p>
                              <table className="w-full">
                                <thead>
                                  <tr>
                                    {['Type', 'Date prévue', 'Montant', 'Date encaissée', 'Montant encaissé', 'Statut', 'Actions'].map(h => (
                                      <th key={h} className="text-left py-1.5 text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider pr-4">{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(plan.schedule_lines || []).map((line, idx) => {
                                    const lineSt = LINE_STATUS_CFG[line.status] || LINE_STATUS_CFG.upcoming;
                                    const typeCfg = TYPE_CFG[line.payment_type] || TYPE_CFG.initial;
                                    const canAct = ['upcoming', 'late'].includes(line.status);
                                    return (
                                      <tr key={line.id} className={`border-t border-gray-100 dark:border-gray-700/50 ${line.status === 'cancelled' ? 'opacity-50' : ''}`} data-testid={`schedule-line-${line.id}`}>
                                        <td className="py-2 pr-4">
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeCfg.cls}`}>
                                            {typeCfg.label} {line.total_installments > 1 ? `${line.installment_number}/${line.total_installments}` : ''}
                                          </span>
                                        </td>
                                        <td className="py-2 pr-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(line.due_date)}</td>
                                        <td className="py-2 pr-4 text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(line.amount)}</td>
                                        <td className="py-2 pr-4 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{line.paid_date ? formatDate(line.paid_date) : '—'}</td>
                                        <td className="py-2 pr-4 text-sm text-green-700 dark:text-green-400 whitespace-nowrap">{line.paid_amount ? formatCurrency(line.paid_amount) : '—'}</td>
                                        <td className="py-2 pr-4">
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${lineSt.cls}`}>{lineSt.label}</span>
                                        </td>
                                        <td className="py-2">
                                          {canAct && (
                                            <div className="flex items-center gap-1.5">
                                              <Button
                                                size="sm"
                                                className="h-6 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                                                onClick={() => openMarkPaid(line)}
                                                data-testid={`mark-paid-${line.id}`}
                                              >
                                                <Check size={11} className="mr-1" /> Payé
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 px-2 text-xs text-gray-400 hover:text-red-500"
                                                onClick={() => setCancelLineData(line)}
                                                data-testid={`cancel-line-${line.id}`}
                                              >
                                                <X size={11} />
                                              </Button>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mark Paid Dialog */}
      <Dialog open={!!markPaidLine} onOpenChange={() => setMarkPaidLine(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Confirmer le paiement</DialogTitle>
          </DialogHeader>
          {markPaidLine && (
            <div className="space-y-3 py-2">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-sm">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full mr-2 ${(TYPE_CFG[markPaidLine.payment_type] || TYPE_CFG.initial).cls}`}>
                  {(TYPE_CFG[markPaidLine.payment_type] || TYPE_CFG.initial).label}
                </span>
                <strong>{formatCurrency(markPaidLine.amount)}</strong> · Échéance du {formatDate(markPaidLine.due_date)}
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Date de paiement</Label>
                <Input
                  type="date"
                  value={markPaidForm.paid_date}
                  onChange={e => setMarkPaidForm(f => ({ ...f, paid_date: e.target.value }))}
                  data-testid="mark-paid-date"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Montant encaissé (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={markPaidForm.paid_amount}
                  onChange={e => setMarkPaidForm(f => ({ ...f, paid_amount: e.target.value }))}
                  data-testid="mark-paid-amount"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidLine(null)}>Annuler</Button>
            <Button onClick={handleMarkPaid} disabled={savingMarkPaid} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-mark-paid">
              <Check size={13} className="mr-1.5" /> {savingMarkPaid ? 'Enregistrement...' : 'Confirmer le paiement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Line Dialog */}
      <Dialog open={!!cancelLineData} onOpenChange={() => setCancelLineData(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Annuler cette échéance ?</DialogTitle>
          </DialogHeader>
          {cancelLineData && (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
              L'échéance de <strong>{formatCurrency(cancelLineData.amount)}</strong> prévue le {formatDate(cancelLineData.due_date)} sera marquée comme annulée.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelLineData(null)}>Conserver</Button>
            <Button onClick={handleCancelLine} disabled={cancellingLine} variant="destructive" data-testid="confirm-cancel-line">
              {cancellingLine ? 'Annulation...' : 'Annuler l\'échéance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete / Cancel Plan Dialog */}
      <Dialog open={!!deletePlanData} onOpenChange={() => setDeletePlanData(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {deletePlanData?.hasPaid ? 'Annuler ce plan ?' : 'Supprimer ce plan ?'}
            </DialogTitle>
          </DialogHeader>
          {deletePlanData && (
            <div className="py-2 space-y-2">
              {deletePlanData.hasPaid ? (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Ce plan contient des paiements enregistrés. Il sera <strong>annulé</strong> (archivé) pour conserver l'historique d'encaissement.
                  </p>
                  <p className="text-xs text-gray-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2.5 text-amber-700 dark:text-amber-400">
                    Les échéances restantes seront marquées "Annulé". L'historique des paiements est conservé.
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Ce plan sera <strong>supprimé définitivement</strong>. Aucun paiement n'a été enregistré sur ce plan.
                </p>
              )}
              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                Plan : <span className="text-[#1B99E5]">{deletePlanData.plan.deal_title}</span>
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePlanData(null)}>Conserver</Button>
            <Button
              onClick={handleDeleteOrCancelPlan}
              disabled={deleting}
              variant={deletePlanData?.hasPaid ? 'outline' : 'destructive'}
              className={deletePlanData?.hasPaid ? 'border-orange-500 text-orange-600 hover:bg-orange-50' : ''}
              data-testid="confirm-delete-plan"
            >
              {deleting ? 'En cours...' : deletePlanData?.hasPaid ? 'Annuler le plan' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
